import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_FACADE_HEIGHT,
  facadeLengthMeters,
  syncFacadeHeights,
} from "../config/facades";
import { getElevation, getReferenceElevation } from "../drawing/utils/elevation";

// ── Fassaden-Tabelle (Zeichen-Schritt 5) ─────────────────────────────
// Listet jede gezeichnete Fassade mit Nummer (passend zum roten Label auf
// der Karte), Höhenlage relativ zum Sportplatz, Fassadenhöhe und Länge;
// je Zeile ein ✕ zum Löschen.
//
// Ergänzt die Live-Anzeige des Zeichenwerkzeugs (der dunkle Kasten oben
// links), die immer nur die GERADE gezeichnete Fassade zeigt: sobald man
// weiterzeichnet, ist die vorige Zahl weg. Die Tabelle hält sie fest und
// macht sie vergleichbar.
//
// Höhenlage: dieselbe Quelle wie die Live-Anzeige und wie das Backend —
// Terrarium-Kacheln über drawing/utils/elevation.js. Kein eigener
// Endpoint, kein Raten-Limit, und was hier steht, ist das, womit später
// gerechnet wird. Bezugspunkte exakt wie in der Rechenkette:
// Fassade = erster Stützpunkt der Polylinie, Feld = Spielfeldniveau.
// Reine Anzeige, nicht überschreibbar.
//
// Fassadenhöhe: editierbar, Vorgabe 8 m (der Wert, der bisher in
// create_geometries.py fest verdrahtet war). Geht als buildingHeights mit
// an /projects/{id}/sketch. ACHTUNG: steuert die Blendungsrechnung NICHT
// — die V3-Kette setzt Fassadenpunkte nur auf der unteren Ebene und liest
// building.height nirgends. Der Wert ist Dokumentation (u. a. für den
// Report), keine Rechengröße.
// ─────────────────────────────────────────────────────────────────────

// Zahl deutsch formatieren: 2.4 → „2,4"
const de = (n, digits = 1) =>
  Number(n).toFixed(digits).replace(".", ",");

// Schlüssel je Fassade: der Startpunkt. Genau dieser Punkt bestimmt die
// Höhenlage, also ändert sich der Wert nur, wenn sich der Punkt ändert.
const keyOf = (line) =>
  line && line.length ? `${line[0][0].toFixed(6)},${line[0][1].toFixed(6)}` : "";

const FacadeTable = ({ geometries, setGeometries, onDelete }) => {
  // useMemo, damit der Höhen-Lookup unten nicht bei jedem Render neu
  // anspringt (ein leeres Fallback-Array wäre jedes Mal eine neue Referenz).
  const lines = useMemo(() => geometries.buildingPolylines || [], [geometries.buildingPolylines]);
  const heights = geometries.buildingHeights || [];
  const fieldRectangle = geometries.fieldRectangle;

  // { [keyOf(line)]: relative Höhe in m | null (Kachel nicht lesbar) }
  const [elevations, setElevations] = useState({});
  const askedRef = useRef(new Set());

  useEffect(() => {
    if (!fieldRectangle || fieldRectangle.length < 3 || lines.length === 0) return;

    const missing = lines.filter((l) => l.length && !askedRef.current.has(keyOf(l)));
    if (missing.length === 0) return;
    missing.forEach((l) => askedRef.current.add(keyOf(l)));

    let cancelled = false;
    (async () => {
      // Bezugshöhe = Spielfeld. Beides läuft über den Kachel-Cache in
      // elevation.js, ist also nach der ersten Fassade praktisch gratis.
      const ref = await getReferenceElevation(fieldRectangle);
      const results = await Promise.all(
        missing.map((l) => getElevation(l[0][0], l[0][1]))
      );
      if (cancelled) return;
      setElevations((prev) => {
        const next = { ...prev };
        missing.forEach((l, i) => {
          const h = results[i];
          next[keyOf(l)] = ref === null || h === null ? null : h - ref;
        });
        return next;
      });
      // Nicht lesbare Kacheln später erneut versuchen dürfen.
      missing.forEach((l, i) => {
        if (ref === null || results[i] === null) askedRef.current.delete(keyOf(l));
      });
    })();

    return () => { cancelled = true; };
    // Absichtlich an der Linienliste hängend, nicht an `geometries`:
    // sonst löst jede Höhen-Eingabe einen neuen Lookup aus.
  }, [lines, fieldRectangle]);

  // Während des Tippens steht der Rohtext hier (index → String). Ohne das
  // würde ein kurzzeitig leeres Feld sofort auf 8 zurückspringen und man
  // käme nie zu einer zweistelligen Eingabe.
  const [drafts, setDrafts] = useState({});

  const parseHeight = (text) => {
    // Deutsche Eingabe „8,5" genauso annehmen wie „8.5"
    const num = parseFloat(String(text).replace(",", "."));
    return Number.isFinite(num) && num > 0 ? num : null;
  };

  const setHeight = (index, text) => {
    setDrafts((d) => ({ ...d, [index]: text }));
    const num = parseHeight(text);
    if (num === null) return;   // unfertige Eingabe stehen lassen
    setGeometries((prev) => {
      const next = syncFacadeHeights(prev.buildingHeights, (prev.buildingPolylines || []).length);
      next[index] = num;
      return { ...prev, buildingHeights: next };
    });
  };

  // Beim Verlassen des Feldes: Unsinn (leer, 0, Text) auf die Vorgabe zurück.
  const commitHeight = (index) => {
    const draft = drafts[index];
    if (draft !== undefined && parseHeight(draft) === null) {
      setGeometries((prev) => {
        const next = syncFacadeHeights(prev.buildingHeights, (prev.buildingPolylines || []).length);
        next[index] = DEFAULT_FACADE_HEIGHT;
        return { ...prev, buildingHeights: next };
      });
    }
    setDrafts((d) => {
      const next = { ...d };
      delete next[index];
      return next;
    });
  };

  // Beim Löschen rutschen alle nachfolgenden Fassaden einen Index nach
  // vorn — offene Roh-Eingaben würden sonst an der falschen Zeile kleben.
  const handleDelete = (index) => {
    setDrafts({});
    onDelete(index);
  };

  if (lines.length === 0) return null;

  const th = {
    textAlign: "left",
    fontWeight: 600,
    color: "#374151",
    padding: "3px 4px",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
  };
  const td = {
    padding: "2px 4px",
    borderBottom: "1px solid #f3f4f6",
    color: "#111827",
    whiteSpace: "nowrap",
  };

  return (
    <div style={{ marginTop: 4 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr>
            <th style={th}>#</th>
            <th style={{ ...th, textAlign: "right" }} title="Geländehöhe an der Fassade relativ zum Spielfeld">
              Höhenlage
            </th>
            <th style={{ ...th, textAlign: "right" }} title="Angenommene Gebäudehöhe — geht in den Report, nicht in die Blendungsrechnung">
              Höhe
            </th>
            <th style={{ ...th, textAlign: "right" }}>Länge</th>
            <th style={th} />
          </tr>
        </thead>
        <tbody>
          {lines.map((line, i) => {
            const key = keyOf(line);
            const rel = key in elevations ? elevations[key] : undefined;
            return (
              <tr key={i}>
                <td style={td}>
                  {/* Gleiche Optik wie das Label auf der Karte */}
                  <span
                    style={{
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      width: 16, height: 16, borderRadius: 8,
                      background: "#FF0000", color: "#fff", fontSize: 10, fontWeight: 600,
                    }}
                  >{i + 1}</span>
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  {rel === undefined ? (
                    <span style={{ color: "#9ca3af" }}>…</span>
                  ) : rel === null ? (
                    <span style={{ color: "#b45309" }} title="Höhenkachel nicht lesbar — die Berechnung fällt dann auf Spielfeldniveau (0,0 m) zurück.">
                      n. v.
                    </span>
                  ) : (
                    <span>{rel >= 0 ? "+" : "−"}{de(Math.abs(rel))} m</span>
                  )}
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={drafts[i] ?? String(heights[i] ?? DEFAULT_FACADE_HEIGHT).replace(".", ",")}
                    onChange={(e) => setHeight(i, e.target.value)}
                    onBlur={() => commitHeight(i)}
                    style={{
                      width: 44, fontSize: 11, textAlign: "right",
                      padding: "1px 3px", border: "1px solid #d1d5db", borderRadius: 3,
                    }}
                  />{" "}m
                </td>
                <td style={{ ...td, textAlign: "right", color: "#6b7280" }}>
                  {de(facadeLengthMeters(line), 0)} m
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  <button
                    onClick={() => handleDelete(i)}
                    title={`Fassade ${i + 1} löschen`}
                    style={{
                      border: "none", background: "transparent", cursor: "pointer",
                      color: "#9ca3af", fontSize: 13, lineHeight: 1, padding: "0 2px",
                    }}
                  >✕</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Ehrliche Einordnung: die Höhe kommt aus einem ~30-m-Raster. Auf
          ebenem Gelände steht deshalb bei allen Fassaden dasselbe. */}
      <p style={{ fontSize: 10, color: "#9ca3af", margin: "3px 0 0", lineHeight: 1.35 }}>
        Höhenlage = Gelände an der Fassade minus Spielfeldniveau (Terrarium,
        ~30 m Raster) — dieselbe Quelle wie die Rechnung. Die Gebäudehöhe ist
        eine Annahme für den Report, keine Rechengröße.
      </p>
    </div>
  );
};

export default FacadeTable;
